import React, { useState, useEffect, useRef } from 'react';
import { Button, Tabs, Spin, notification, Tooltip, Popconfirm } from 'antd';
import { DownloadOutlined, UploadOutlined, DeleteOutlined, FileExcelOutlined, PlusOutlined } from '@ant-design/icons';
import { useUser } from '../UserContext';
import { BASE_URL } from '../conf/config';

const prettifyName = (filename) => {
    const chinaMatch = filename.match(/^china_import_template_(.+)\.xlsx$/);
    if (chinaMatch) return `China Import — ${chinaMatch[1]}`;
    return filename
        .replace(/_template\.(html|xlsx)$/, '')
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const isValidTemplateName = (filename, category) => {
    if (category === 'html') return filename.endsWith('_template.html');
    return filename.endsWith('_template.xlsx') ||
        (filename.startsWith('china_import_template_') && filename.endsWith('.xlsx'));
};

const FilesPage = () => {
    const { fetchWithAuth, userGroups } = useUser();
    const [htmlFiles, setHtmlFiles] = useState([]);
    const [xlsFiles, setXlsFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = () => setRefreshKey(k => k + 1);

    const fetchFiles = (category) =>
        fetchWithAuth(`${BASE_URL}/api/templates/?category=${category}`)
            .then(r => r.json())
            .then(d => d.files || []);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchFiles('html'), fetchFiles('xls')])
            .then(([html, xls]) => { setHtmlFiles(html); setXlsFiles(xls); })
            .finally(() => setLoading(false));
    }, [refreshKey]);

    if (!userGroups.includes('admin')) return (
        <div style={{ maxWidth: 750, margin: '0 auto', paddingTop: 40 }}>
            <h2>Files</h2>
            <p>Your generated files will appear here.</p>
        </div>
    );

    const handleDownload = async (filename, category) => {
        const res = await fetchWithAuth(
            `${BASE_URL}/api/templates/?download=${encodeURIComponent(filename)}&category=${category}`
        );
        if (!res.ok) { notification.error({ message: 'Download failed' }); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); window.URL.revokeObjectURL(url);
    };

    const handleUpload = (filename, category, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', filename);
        formData.append('category', category);
        fetchWithAuth(`${BASE_URL}/api/templates/`, { method: 'POST', body: formData })
            .then(r => r.json())
            .then(data => {
                if (data.success) { notification.success({ message: `${prettifyName(filename)} updated successfully` }); refresh(); }
                else notification.error({ message: 'Upload failed', description: data.error });
            })
            .catch(() => notification.error({ message: 'Upload failed' }));
    };

    const handleUploadNew = (category, file) => {
        if (!isValidTemplateName(file.name, category)) {
            const hint = category === 'html'
                ? 'File must be named like: my_template.html'
                : 'File must be named like: my_template.xlsx or china_import_template_Name.xlsx';
            notification.error({ message: 'Invalid filename', description: hint });
            return;
        }
        handleUpload(file.name, category, file);
    };

    const handleDelete = (filename, category) => {
        fetchWithAuth(
            `${BASE_URL}/api/templates/?filename=${encodeURIComponent(filename)}&category=${category}`,
            { method: 'DELETE' }
        )
            .then(r => r.json())
            .then(data => {
                if (data.success) { notification.success({ message: `${prettifyName(filename)} deleted` }); refresh(); }
                else notification.error({ message: 'Delete failed', description: data.error });
            })
            .catch(() => notification.error({ message: 'Delete failed' }));
    };

    const FileRow = ({ filename, category }) => {
        const inputRef = useRef();
        const accept = category === 'html' ? '.html' : '.xlsx';
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 8px', borderBottom: '1px solid #f0f0f0',
            }}>
                <span style={{ flex: 1, fontSize: 14 }}>{prettifyName(filename)}</span>
                <Tooltip title={`Download ${prettifyName(filename)}`} placement="left">
                    <Button icon={<DownloadOutlined />} onClick={() => handleDownload(filename, category)} />
                </Tooltip>
                <Tooltip title="Replace — upload any file, it overwrites this template regardless of filename" placement="left">
                    <Button icon={<UploadOutlined />} type="primary" onClick={() => inputRef.current.click()} />
                </Tooltip>
                <Popconfirm
                    title={`Delete ${prettifyName(filename)}?`}
                    description="This cannot be undone."
                    onConfirm={() => handleDelete(filename, category)}
                    okText="Delete" okType="danger" cancelText="Cancel"
                >
                    <Tooltip title="Delete template" placement="left">
                        <Button icon={<DeleteOutlined />} danger />
                    </Tooltip>
                </Popconfirm>
                <input
                    type="file" accept={accept} style={{ display: 'none' }} ref={inputRef}
                    onChange={e => {
                        if (e.target.files[0]) { handleUpload(filename, category, e.target.files[0]); e.target.value = ''; }
                    }}
                />
            </div>
        );
    };

    const UploadNewButton = ({ category }) => {
        const inputRef = useRef();
        const accept = category === 'html' ? '.html' : '.xlsx';
        const hint = category === 'html'
            ? 'Filename must match: *_template.html'
            : 'Filename must match: *_template.xlsx or china_import_template_*.xlsx';
        return (
            <div style={{ marginTop: 16 }} data-help-id="files-new-template">
                <Tooltip title={hint} placement="right">
                    <Button icon={<PlusOutlined />} onClick={() => inputRef.current.click()}>
                        Upload new template
                    </Button>
                </Tooltip>
                <input
                    type="file" accept={accept} style={{ display: 'none' }} ref={inputRef}
                    onChange={e => {
                        if (e.target.files[0]) { handleUploadNew(category, e.target.files[0]); e.target.value = ''; }
                    }}
                />
            </div>
        );
    };

    if (loading) return <Spin style={{ display: 'block', margin: '60px auto' }} />;

    const tabItems = [
        {
            key: 'html',
            label: 'PDF Templates',
            children: (
                <div data-help-id="files-template-list">
                    {htmlFiles.map(f => <FileRow key={f} filename={f} category="html" />)}
                    <UploadNewButton category="html" />
                </div>
            ),
        },
        {
            key: 'xls',
            label: <span><FileExcelOutlined /> Excel Templates</span>,
            children: (
                <div data-help-id="files-template-list">
                    {xlsFiles.map(f => <FileRow key={f} filename={f} category="xls" />)}
                    <UploadNewButton category="xls" />
                </div>
            ),
        },
    ];

    return (
        <div style={{ maxWidth: 750, margin: '0 auto', paddingBottom: 40 }}>
            <h2>Files</h2>
            <Tabs items={tabItems} />
        </div>
    );
};

export default FilesPage;
